import { useEffect, useRef } from "react";
import { animate } from "animejs";
import { prefersReducedMotion } from "@/lib/anime/prefersReducedMotion";

/**
 * Hero trust-signal chips · small floating callouts that tie the
 * subtitle copy ("FMCSA, insurance, bonds, fraud signals, and Canadian
 * federal data") to the wireframe globe behind them. They sit in the
 * upper corners of the hero so they read as orbiting indicators of the
 * checks the platform performs.
 *
 * Lives outside the <Canvas> so it doesn't fight WebGL state. Three
 * chips max · enough to anchor the copy, not enough to feel like a
 * carousel.
 */
const CHIPS = [
  { label: "FMCSA verified", x: "6%", y: "22%" },
  { label: "Insurance current", x: "82%", y: "30%" },
  { label: "Canadian federal data", x: "8%", y: "74%" },
] as const;

export function HeroTrustChips() {
  const refs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    const chips = refs.current.filter((c): c is HTMLDivElement => c !== null);
    if (chips.length === 0) return;

    if (prefersReducedMotion()) {
      chips.forEach((c) => {
        c.style.opacity = "1";
        c.style.transform = "none";
      });
      return;
    }

    chips.forEach((chip, i) => {
      // Entry · gentle fade-up, staggered.
      animate(chip, {
        opacity: [0, 1],
        translateY: [14, 0],
        scale: [0.92, 1],
        duration: 900,
        delay: 700 + i * 160,
        ease: "outCubic",
      });
      // Perpetual drift · each chip on its own period so they never
      // line up. Subtle vertical breathing only.
      animate(chip, {
        translateY: [
          { to: -6, duration: 2600 + i * 320 },
          { to: 6, duration: 2600 + i * 320 },
        ],
        loop: true,
        delay: 1600 + i * 200,
        ease: "inOutSine",
      });
    });
  }, []);

  return (
    <div className="absolute inset-0 pointer-events-none select-none hidden lg:block" aria-hidden="true">
      {CHIPS.map((chip, i) => (
        <div
          key={chip.label}
          ref={(el) => (refs.current[i] = el)}
          className="absolute flex items-center gap-2 px-3 py-1.5 rounded-full bg-card/60 backdrop-blur-md border border-primary/25 text-[11px] font-mono uppercase tracking-wider text-foreground/85 opacity-0 shadow-[0_8px_30px_-12px_hsl(var(--primary)/0.5)]"
          style={{ left: chip.x, top: chip.y }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_8px_hsl(var(--primary))]" />
          {chip.label}
        </div>
      ))}
    </div>
  );
}

export default HeroTrustChips;
